// Notification Container - Scrollable feed of all notifications
import React, { useState } from 'react';
import { GlassNotificationToast } from './GlassNotificationToast';
import type { NotificationType } from '@/entities/Notification';
import { Bell, X } from 'lucide-react';

interface NotificationContainerProps {
  notifications: NotificationType[];
  onDismiss: (id: number) => void;
  onClick: (notification: NotificationType) => void;
}

export function NotificationContainer({ 
  notifications, 
  onDismiss, 
  onClick 
}: NotificationContainerProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  
  // Show undismissed notifications (including read ones)
  const activeNotifications = notifications
    .filter(n => !n.dismissed) // Only filter by dismissed, show read notifications
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()); // Newest first

  const unreadCount = activeNotifications.filter(n => !n.read).length;

  // Debug logging
  console.log('🎯 NotificationContainer:', {
    totalReceived: notifications.length,
    activeCount: activeNotifications.length,
    unreadCount,
    isCollapsed,
    notifications: activeNotifications,
    sample: notifications[0] // Show first notification structure
  });

  if (activeNotifications.length === 0) {
    console.log('🎯 No active notifications - container hidden');
    return null;
  }

  // Collapsed view - just bell icon with badge - MOBILE ONLY
  if (isCollapsed) {
    return (
      <button
        onClick={() => setIsCollapsed(false)}
        className="md:hidden fixed left-1/2 -translate-x-1/2 z-[9999] pointer-events-auto"
        style={{ bottom: '120px' }}
      >
        <div className="relative p-3 rounded-full bg-slate-800/80 backdrop-blur-xl border border-slate-600/50 hover:bg-slate-700/80 transition-all shadow-lg">
          <Bell className="w-6 h-6 text-slate-200" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
      </button>
    );
  }

  console.log('🎯 Rendering', activeNotifications.length, 'notification(s) as individual cards');

  // Sort: newest first (at top)
  const sortedNotifications = [...activeNotifications].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  // iPhone-style: Desktop = LEFT edge of screen, Mobile = TOP center
  return (
    <div 
      className="fixed z-[9999] pointer-events-none flex flex-col gap-3"
      style={{ 
        // Desktop: left edge of screen (outside organizer), Mobile: top center
        left: window.innerWidth >= 768 ? '420px' : '50%',
        top: window.innerWidth >= 768 ? '50%' : '20px',
        transform: window.innerWidth >= 768 ? 'translateY(-50%)' : 'translateX(-50%)',
        width: window.innerWidth >= 768 ? '360px' : 'calc(100vw - 32px)',
        maxWidth: '400px',
        maxHeight: 'calc(100vh - 100px)'
      }}
    >
      {sortedNotifications.map((notification, index) => (
        <div 
          key={notification.id} 
          className="pointer-events-auto"
          style={{
            animation: window.innerWidth >= 768 ? 'slideInFromTop 0.4s ease-out' : 'slideInFromTop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
            animationDelay: `${index * 0.1}s`,
            animationFillMode: 'both'
          }}
        >
          <GlassNotificationToast
            notification={notification}
            onDismiss={onDismiss}
            onClick={onClick}
            index={index}
          />
        </div>
      ))}
    </div>
  );
}
